import axiosInstance from '../../../shared/api/axiosInstance';
import type { SoftwareFactoryDashboard } from '../../../generated/api/models/softwareFactoryDashboard';
import type { RoadmapHorizon } from '../constants';
import type {
  ExceptionFingerprint,
  ExceptionLogSource,
  ExceptionLogSourceCreate,
  ExceptionLogSourceUpdate,
  ExceptionStats,
  ListAnnouncementsParams,
  ListFingerprintsParams,
  ListModulesParams,
  ListResponse,
  LinkedTicketInfo,
  ListWorkItemsParams,
  SfChangelogEntry,
  SfChangelogEntryCreate,
  SfUserAnnouncement,
  SfUserAnnouncementCreate,
  SfUserAnnouncementDelivery,
  SfUserAnnouncementTranslation,
  SfUserAnnouncementTranslationInput,
  SfUserAnnouncementUpdate,
  SfWorkItemCommit,
  SfWorkItemCustomer,
  PortfolioResponse,
  SfInitiative,
  SfInitiativeCreate,
  SfOkrObjective,
  SfInitiativeCustomer,
  SfMilestone,
  SfMilestoneCreate,
  SfMilestoneUpdate,
  MilestoneLinkedWorkItem,
  SfModule,
  SfModuleCreate,
  SfModuleListResponse,
  SfModuleUpdate,
  SfWorkItem,
  SfWorkItemCreate,
  SfWorkItemTransition,
  SfWorkItemUpdate,
  SfSprint,
  SfSprintCreate,
  SfSprintUpdate,
  ListSprintsParams,
  SprintBoardResponse,
  SprintCompleteResult,
  SprintInitializeResult,
  ShippingReport,
  ShippingReportParams,
  WorkItemTransitionData,
} from '../types';

const BASE_URL = '/api/v1/software-factory';

export const moduleApi = {
  list: async (params?: ListModulesParams): Promise<SfModuleListResponse> => {
    const response = await axiosInstance.get(`${BASE_URL}/modules`, { params });
    return response.data;
  },
  get: async (id: string): Promise<SfModule> => {
    const response = await axiosInstance.get(`${BASE_URL}/modules/${id}`);
    return response.data;
  },
  create: async (data: SfModuleCreate): Promise<SfModule> => {
    const response = await axiosInstance.post(`${BASE_URL}/modules`, data);
    return response.data;
  },
  update: async (id: string, data: SfModuleUpdate): Promise<SfModule> => {
    const response = await axiosInstance.put(`${BASE_URL}/modules/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/modules/${id}`);
  },
  reorder: async (moduleIds: string[]): Promise<void> => {
    await axiosInstance.put(`${BASE_URL}/modules/reorder`, { module_ids: moduleIds });
  },
};

export const workItemApi = {
  list: async (params?: ListWorkItemsParams): Promise<ListResponse<SfWorkItem>> => {
    const response = await axiosInstance.get(`${BASE_URL}/work-items`, {
      params,
      paramsSerializer: { indexes: null },
    });
    return response.data;
  },
  get: async (id: string): Promise<SfWorkItem> => {
    const response = await axiosInstance.get(`${BASE_URL}/work-items/${id}`);
    return response.data;
  },
  create: async (data: SfWorkItemCreate): Promise<SfWorkItem> => {
    const response = await axiosInstance.post(`${BASE_URL}/work-items`, data);
    return response.data;
  },
  update: async (id: string, data: SfWorkItemUpdate): Promise<SfWorkItem> => {
    const response = await axiosInstance.put(`${BASE_URL}/work-items/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/work-items/${id}`);
  },
  updateStatus: async (id: string, status: string): Promise<SfWorkItem> => {
    const response = await axiosInstance.put(`${BASE_URL}/work-items/${id}/status`, { status });
    return response.data;
  },
  transitionStatus: async (id: string, data: WorkItemTransitionData): Promise<SfWorkItem> => {
    const response = await axiosInstance.put(`${BASE_URL}/work-items/${id}/status`, data);
    return response.data;
  },
  listTransitions: async (id: string): Promise<{ items: SfWorkItemTransition[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/work-items/${id}/transitions`);
    return response.data;
  },
  rebaseline: async (id: string): Promise<SfWorkItem> => {
    const response = await axiosInstance.post(`${BASE_URL}/work-items/${id}/rebaseline`);
    return response.data;
  },
  vote: async (id: string): Promise<SfWorkItem> => {
    const response = await axiosInstance.post(`${BASE_URL}/work-items/${id}/vote`);
    return response.data;
  },
  removeVote: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/work-items/${id}/vote`);
  },
  dispatch: async (id: string, agentId?: string, agentLabel?: string): Promise<SfWorkItem> => {
    const response = await axiosInstance.post(`${BASE_URL}/work-items/${id}/dispatch`, { agent_id: agentId, agent_label: agentLabel });
    return response.data;
  },
  listDispatchable: async (params?: { limit?: number; offset?: number }): Promise<ListResponse<SfWorkItem>> => {
    const response = await axiosInstance.get(`${BASE_URL}/work-items/dispatchable`, { params });
    return response.data;
  },
  listChildren: async (id: string, params?: { limit?: number }): Promise<ListResponse<SfWorkItem>> => {
    const response = await axiosInstance.get(`${BASE_URL}/work-items/${id}/children`, { params });
    return response.data;
  },
  listDispatchQueue: async (params?: {
    autonomy_level?: string[];
    status?: string[];
    limit?: number;
  }): Promise<ListResponse<SfWorkItem>> => {
    const response = await axiosInstance.get(`${BASE_URL}/work-items/dispatch-queue`, {
      params,
      paramsSerializer: { indexes: null },
    });
    return response.data;
  },
  markDispatched: async (
    id: string,
    data: { slot: string; worktree?: string | null; session_id?: string | null },
  ): Promise<SfWorkItem> => {
    const response = await axiosInstance.post(`${BASE_URL}/work-items/${id}/mark-dispatched`, data);
    return response.data;
  },
  clearDispatch: async (id: string): Promise<SfWorkItem> => {
    const response = await axiosInstance.post(`${BASE_URL}/work-items/${id}/clear-dispatch`);
    return response.data;
  },
  getCommits: async (id: string): Promise<SfWorkItemCommit[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/work-items/${id}/commits`);
    return response.data;
  },
  linkCommits: async (id: string, shas: string[]): Promise<{ ok: boolean }> => {
    const response = await axiosInstance.post(`${BASE_URL}/work-items/${id}/commits`, { shas });
    return response.data;
  },
  unlinkCommit: async (id: string, sha: string): Promise<{ ok: boolean }> => {
    const response = await axiosInstance.delete(`${BASE_URL}/work-items/${id}/commits/${sha}`);
    return response.data;
  },
  getLinkedTicket: async (id: string): Promise<{ linked_ticket: LinkedTicketInfo | null }> => {
    const response = await axiosInstance.get(`${BASE_URL}/work-items/${id}/linked-ticket`);
    return response.data;
  },
  listCustomers: async (id: string): Promise<{ items: SfWorkItemCustomer[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/work-items/${id}/customers`);
    return response.data;
  },
  addCustomer: async (
    id: string,
    data: {
      organization_id?: string | null;
      contact_id?: string | null;
      deal_id?: string | null;
    },
  ): Promise<SfWorkItemCustomer> => {
    const response = await axiosInstance.post(`${BASE_URL}/work-items/${id}/customers`, data);
    return response.data;
  },
  removeCustomer: async (id: string, customerId: string): Promise<{ ok: boolean }> => {
    const response = await axiosInstance.delete(
      `${BASE_URL}/work-items/${id}/customers/${customerId}`,
    );
    return response.data;
  },
};

export const initiativeApi = {
  list: async (params?: { search?: string; status?: string; module_id?: string; parent_id?: string; priority?: number; roadmap_horizon?: RoadmapHorizon; customer_organization_id?: string; customer_contact_id?: string; customer_deal_id?: string; limit?: number; offset?: number }): Promise<ListResponse<SfInitiative>> => {
    const response = await axiosInstance.get(`${BASE_URL}/initiatives`, { params });
    return response.data;
  },
  listCustomers: async (id: string): Promise<{ items: SfInitiativeCustomer[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/initiatives/${id}/customers`);
    return response.data;
  },
  listOkrObjectives: async (): Promise<{ objectives: SfOkrObjective[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/initiatives/okr-objectives`);
    return response.data;
  },
  addCustomer: async (
    id: string,
    data: {
      organization_id?: string | null;
      contact_id?: string | null;
      deal_id?: string | null;
    },
  ): Promise<SfInitiativeCustomer> => {
    const response = await axiosInstance.post(`${BASE_URL}/initiatives/${id}/customers`, data);
    return response.data;
  },
  removeCustomer: async (id: string, customerId: string): Promise<{ ok: boolean }> => {
    const response = await axiosInstance.delete(
      `${BASE_URL}/initiatives/${id}/customers/${customerId}`,
    );
    return response.data;
  },
  get: async (id: string): Promise<SfInitiative> => {
    const response = await axiosInstance.get(`${BASE_URL}/initiatives/${id}`);
    return response.data;
  },
  create: async (data: SfInitiativeCreate): Promise<SfInitiative> => {
    const response = await axiosInstance.post(`${BASE_URL}/initiatives`, data);
    return response.data;
  },
  update: async (id: string, data: Partial<SfInitiativeCreate>): Promise<SfInitiative> => {
    const response = await axiosInstance.put(`${BASE_URL}/initiatives/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/initiatives/${id}`);
  },
  rebaseline: async (id: string): Promise<SfInitiative> => {
    const response = await axiosInstance.post(`${BASE_URL}/initiatives/${id}/rebaseline`);
    return response.data;
  },
  suggestWorkItems: async (id: string, context?: string): Promise<{ data: { suggestions: WorkItemSuggestion[] } }> => {
    const response = await axiosInstance.post(`${BASE_URL}/initiatives/${id}/ai/suggest-work-items`, context ? { context } : {});
    return response.data;
  },
};

export interface WorkItemSuggestion {
  title: string;
  description: string | null;
  type: string;
  priority: number;
  size: string;
}

export const milestoneApi = {
  list: async (params?: { search?: string; status?: string; module_id?: string; limit?: number; offset?: number }): Promise<ListResponse<SfMilestone>> => {
    const response = await axiosInstance.get(`${BASE_URL}/milestones`, { params });
    return response.data;
  },
  get: async (id: string): Promise<SfMilestone> => {
    const response = await axiosInstance.get(`${BASE_URL}/milestones/${id}`);
    return response.data;
  },
  create: async (data: SfMilestoneCreate): Promise<SfMilestone> => {
    const response = await axiosInstance.post(`${BASE_URL}/milestones`, data);
    return response.data;
  },
  update: async (id: string, data: SfMilestoneUpdate): Promise<SfMilestone> => {
    const response = await axiosInstance.put(`${BASE_URL}/milestones/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/milestones/${id}`);
  },
  listWorkItems: async (id: string): Promise<ListResponse<MilestoneLinkedWorkItem>> => {
    const response = await axiosInstance.get(`${BASE_URL}/milestones/${id}/work-items`);
    return response.data;
  },
  linkWorkItem: async (id: string, workItemId: string): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/milestones/${id}/work-items`, { work_item_id: workItemId });
  },
  unlinkWorkItem: async (id: string, workItemId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/milestones/${id}/work-items/${workItemId}`);
  },
};

export const changelogApi = {
  list: async (params?: { search?: string; category?: string; module_id?: string; published_only?: boolean; limit?: number; offset?: number }): Promise<ListResponse<SfChangelogEntry>> => {
    const response = await axiosInstance.get(`${BASE_URL}/changelog`, { params });
    return response.data;
  },
  get: async (id: string): Promise<SfChangelogEntry> => {
    const response = await axiosInstance.get(`${BASE_URL}/changelog/${id}`);
    return response.data;
  },
  create: async (data: SfChangelogEntryCreate): Promise<SfChangelogEntry> => {
    const response = await axiosInstance.post(`${BASE_URL}/changelog`, data);
    return response.data;
  },
  update: async (id: string, data: Partial<SfChangelogEntryCreate>): Promise<SfChangelogEntry> => {
    const response = await axiosInstance.put(`${BASE_URL}/changelog/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/changelog/${id}`);
  },
  publish: async (id: string): Promise<SfChangelogEntry> => {
    const response = await axiosInstance.post(`${BASE_URL}/changelog/${id}/publish`);
    return response.data;
  },
  markRead: async (id: string): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/changelog/${id}/read`);
  },
  listUnread: async (params?: { module_id?: string; limit?: number }): Promise<SfChangelogEntry[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/changelog/unread`, { params });
    return response.data;
  },
};


/**
 * User announcements. Management (CRUD, translations, links) is general — any
 * Software Factory tenant can author. Delivery (`listMine` / `recordView`) is
 * the API consumed by PanLuma's own login modal + bell.
 * See docs/platform/user-announcements.md.
 */
export const announcementApi = {
  list: async (params?: ListAnnouncementsParams): Promise<ListResponse<SfUserAnnouncement>> => {
    const response = await axiosInstance.get(`${BASE_URL}/announcements`, { params });
    return response.data;
  },
  get: async (id: string): Promise<SfUserAnnouncement> => {
    const response = await axiosInstance.get(`${BASE_URL}/announcements/${id}`);
    return response.data;
  },
  create: async (data: SfUserAnnouncementCreate): Promise<SfUserAnnouncement> => {
    const response = await axiosInstance.post(`${BASE_URL}/announcements`, data);
    return response.data;
  },
  update: async (id: string, data: SfUserAnnouncementUpdate): Promise<SfUserAnnouncement> => {
    const response = await axiosInstance.put(`${BASE_URL}/announcements/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/announcements/${id}`);
  },
  publish: async (id: string): Promise<SfUserAnnouncement> => {
    const response = await axiosInstance.post(`${BASE_URL}/announcements/${id}/publish`);
    return response.data;
  },
  unpublish: async (id: string): Promise<SfUserAnnouncement> => {
    const response = await axiosInstance.post(`${BASE_URL}/announcements/${id}/unpublish`);
    return response.data;
  },
  archive: async (id: string): Promise<SfUserAnnouncement> => {
    const response = await axiosInstance.post(`${BASE_URL}/announcements/${id}/archive`);
    return response.data;
  },
  upsertTranslation: async (
    id: string,
    language: string,
    data: SfUserAnnouncementTranslationInput,
  ): Promise<SfUserAnnouncementTranslation> => {
    const response = await axiosInstance.put(
      `${BASE_URL}/announcements/${id}/translations/${language}`,
      data,
    );
    return response.data;
  },
  deleteTranslation: async (id: string, language: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/announcements/${id}/translations/${language}`);
  },
  setWorkItems: async (id: string, workItemIds: string[]): Promise<void> => {
    await axiosInstance.put(`${BASE_URL}/announcements/${id}/work-items`, {
      work_item_ids: workItemIds,
    });
  },
  setInitiatives: async (id: string, initiativeIds: string[]): Promise<void> => {
    await axiosInstance.put(`${BASE_URL}/announcements/${id}/initiatives`, {
      initiative_ids: initiativeIds,
    });
  },
  listMine: async (language: string): Promise<SfUserAnnouncementDelivery[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/announcements/me`, {
      params: { language },
    });
    return response.data;
  },
  recordView: async (id: string, dismissed: boolean): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/announcements/${id}/view`, { dismissed });
  },
};

export const dashboardApi = {
  getStats: async (): Promise<SoftwareFactoryDashboard> => {
    const response = await axiosInstance.get(`${BASE_URL}/dashboard`);
    return response.data;
  },
};

export const portfolioApi = {
  get: async (params?: { include_archived?: boolean }): Promise<PortfolioResponse> => {
    const response = await axiosInstance.get(`${BASE_URL}/portfolio`, { params });
    return response.data;
  },
};

export const reportsApi = {
  getShippingReport: async (params: ShippingReportParams): Promise<ShippingReport> => {
    const response = await axiosInstance.get(`${BASE_URL}/reports/shipping`, { params });
    return response.data;
  },
};

export const settingsApi = {
  getWorkItemSizes: async (): Promise<string[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/settings/work-item-sizes`);
    return response.data.work_item_sizes;
  },
  getUserSettings: async (): Promise<Record<string, unknown>> => {
    const response = await axiosInstance.get(`${BASE_URL}/settings/user`);
    return response.data;
  },
  updateUserSettings: async (data: Record<string, unknown>): Promise<Record<string, unknown>> => {
    const response = await axiosInstance.put(`${BASE_URL}/settings/user`, data);
    return response.data;
  },
  getTenantSettings: async (): Promise<Record<string, unknown>> => {
    const response = await axiosInstance.get(`${BASE_URL}/settings/tenant`);
    return response.data;
  },
  updateTenantSettings: async (data: Record<string, unknown>): Promise<Record<string, unknown>> => {
    const response = await axiosInstance.put(`${BASE_URL}/settings/tenant`, data);
    return response.data;
  },
};

// -- Exception Monitoring --

export const exceptionLogSourceApi = {
  list: async (): Promise<ExceptionLogSource[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/exceptions/log-sources`);
    return response.data;
  },
  create: async (data: ExceptionLogSourceCreate): Promise<ExceptionLogSource> => {
    const response = await axiosInstance.post(`${BASE_URL}/exceptions/log-sources`, data);
    return response.data;
  },
  update: async (id: string, data: ExceptionLogSourceUpdate): Promise<ExceptionLogSource> => {
    const response = await axiosInstance.put(`${BASE_URL}/exceptions/log-sources/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/exceptions/log-sources/${id}`);
  },
  test: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/exceptions/log-sources/${id}/test`);
    return response.data;
  },
};

export const exceptionFingerprintApi = {
  list: async (params?: ListFingerprintsParams): Promise<ListResponse<ExceptionFingerprint>> => {
    const response = await axiosInstance.get(`${BASE_URL}/exceptions/fingerprints`, { params });
    return response.data;
  },
  get: async (id: string): Promise<ExceptionFingerprint> => {
    const response = await axiosInstance.get(`${BASE_URL}/exceptions/fingerprints/${id}`);
    return response.data;
  },
  mute: async (id: string, reason?: string): Promise<ExceptionFingerprint> => {
    const response = await axiosInstance.put(`${BASE_URL}/exceptions/fingerprints/${id}/mute`, { reason });
    return response.data;
  },
  unmute: async (id: string): Promise<ExceptionFingerprint> => {
    const response = await axiosInstance.put(`${BASE_URL}/exceptions/fingerprints/${id}/unmute`);
    return response.data;
  },
  resolve: async (id: string): Promise<ExceptionFingerprint> => {
    const response = await axiosInstance.put(`${BASE_URL}/exceptions/fingerprints/${id}/resolve`);
    return response.data;
  },
  createWorkItem: async (id: string): Promise<Record<string, unknown>> => {
    const response = await axiosInstance.post(`${BASE_URL}/exceptions/fingerprints/${id}/create-item`);
    return response.data;
  },
};

export const exceptionStatsApi = {
  get: async (): Promise<ExceptionStats> => {
    const response = await axiosInstance.get(`${BASE_URL}/exceptions/stats`);
    return response.data;
  },
};

export const sprintApi = {
  list: async (params?: ListSprintsParams): Promise<ListResponse<SfSprint>> => {
    const response = await axiosInstance.get(`${BASE_URL}/sprints`, { params });
    return response.data;
  },
  getActive: async (): Promise<SfSprint | null> => {
    const response = await axiosInstance.get(`${BASE_URL}/sprints/active`);
    return response.data;
  },
  initialize: async (startDate?: string): Promise<SprintInitializeResult> => {
    const response = await axiosInstance.post(`${BASE_URL}/sprints/initialize`, {
      start_date: startDate ?? null,
    });
    return response.data;
  },
  get: async (id: string): Promise<SfSprint> => {
    const response = await axiosInstance.get(`${BASE_URL}/sprints/${id}`);
    return response.data;
  },
  getBoard: async (id: string): Promise<SprintBoardResponse> => {
    const response = await axiosInstance.get(`${BASE_URL}/sprints/${id}/board`);
    return response.data;
  },
  create: async (data: SfSprintCreate): Promise<SfSprint> => {
    const response = await axiosInstance.post(`${BASE_URL}/sprints`, data);
    return response.data;
  },
  update: async (id: string, data: SfSprintUpdate): Promise<SfSprint> => {
    const response = await axiosInstance.put(`${BASE_URL}/sprints/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/sprints/${id}`);
  },
  complete: async (id: string): Promise<SprintCompleteResult> => {
    const response = await axiosInstance.post(`${BASE_URL}/sprints/${id}/complete`);
    return response.data;
  },
  assignWorkItem: async (sprintId: string, workItemId: string): Promise<SfWorkItem> => {
    const response = await axiosInstance.post(`${BASE_URL}/sprints/${sprintId}/work-items`, {
      work_item_id: workItemId,
    });
    return response.data;
  },
  unassignWorkItem: async (sprintId: string, workItemId: string): Promise<SfWorkItem> => {
    const response = await axiosInstance.delete(
      `${BASE_URL}/sprints/${sprintId}/work-items/${workItemId}`,
    );
    return response.data;
  },
};
